import React, { useState, useEffect } from 'react';
import { NumericFormat } from 'react-number-format';
import TickerRow from '../../../TickerRow/TickerRow';
import { useAuth } from 'react-oidc-context';
import styles from './style/NewPortfolioModal.module.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // 💡 useTranslation 훅 임포트

const API_URL = process.env.REACT_APP_API_BASE_URL;

const MessageModal = ({ message, onClose, isError = false, isSuccess = false }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.messageModalBackdrop}>
      <div className={`${styles.messageModalContent} ${isError ? styles.messageModalError : ''} ${isSuccess ? styles.messageModalSuccess : ''}`}>
        <p>{message}</p>
        <button onClick={onClose} className={styles.messageModalCloseButton}>{t('newPortfolioModal.confirmButton')}</button>
      </div>
    </div>
  );
};

export default function NewPortfolioModal({ 
  onClose, 
  onCreated, 
  initialHoldings = [], 
  initialName = '' ,
  isForcedPublic = true

}) {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(initialName);
  const [holdings, setHoldings] = useState(
    initialHoldings.length > 0 ? initialHoldings : [{ ticker: '', weight: 0 }]
  );
  // isForcedPublic이 false인 일반 저장 모드에서는 사용자가 선택 가능
  const [isPrivate, setIsPrivate] = useState(true); 
  
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalContent, setMessageModalContent] = useState('');
  const [isErrorMessage, setIsErrorMessage] = useState(false);
  const [isSuccessMessage, setIsSuccessMessage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); 

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      sessionStorage.setItem('redirect_after_login', window.location.pathname);
      auth.signinRedirect();
    }
  }, [auth, navigate]);

  const showCustomAlert = (messageKey, isError = false, isSuccess = false) => {
    setMessageModalContent(t(messageKey));
    setIsErrorMessage(isError);
    setIsSuccessMessage(isSuccess);
    setShowMessageModal(true);
  };

  const closeCustomAlert = () => {
    setShowMessageModal(false);
    setMessageModalContent('');
    setIsErrorMessage(false);
    setIsSuccessMessage(false);
  };

  const addHolding = () => setHoldings([...holdings, { ticker: '', weight: 0 }]);

  const updateHolding = (idx, key, val) => {
    const arr = [...holdings];
    arr[idx][key] = key === 'ticker' ? val : parseFloat(val) || 0;
    setHoldings(arr);
  };

  const removeHolding = idx => setHoldings(holdings.filter((_, i) => i !== idx));

  const totalWeight = holdings.reduce((sum, h) => sum + h.weight, 0);
  const isTotalWeightValid = Math.abs(totalWeight - 1) < 1e-6;

  const handleSubmit = async () => {
    if (isSubmitting) return; 

    // 💡 추가: 제출 시작
    setIsSubmitting(true); 

    if (!auth.isAuthenticated) {
      showCustomAlert('newPortfolioModal.modalMessage.loginRequired', true);
      auth.signinRedirect();
      return;
    }

    if (!name.trim()) {
      showCustomAlert('newPortfolioModal.modalMessage.enterName', true);
      return;
    }

    const hasInvalidHolding = holdings.some(h => !h.ticker.trim() || (!h.ticker.trim() && h.weight > 0));
    if (hasInvalidHolding) {
      showCustomAlert('newPortfolioModal.modalMessage.enterTicker', true);
      return;
    }

    if (!isTotalWeightValid) {
      showCustomAlert('newPortfolioModal.modalMessage.totalWeight', true);
      return;
    }


    const payload = holdings.map(h => ({ ticker: h.ticker, weight: h.weight }));
    const finalIsPrivate = isForcedPublic ? false : isPrivate;


    try {
      const idToken = auth.user.id_token;
      const res = await fetch(`${API_URL}/portfolios`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          name: name.trim(),
          holdings: payload,
          isPrivate: finalIsPrivate, 
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
         if (errorData && errorData.error) {
          const errorMessage = errorData.error;
          if (errorMessage.includes('up to 3 public portfolios')) {
            showCustomAlert('newPortfolioModal.modalMessage.limitExceededPublic', true); 
          } else if (errorMessage.includes('up to 10 private portfolios')) {
            showCustomAlert('newPortfolioModal.modalMessage.limitExceededPrivate', true); 
          } else {
            showCustomAlert('newPortfolioModal.modalMessage.serverError', true);
          }
        } else {
          throw new Error(`Failed to create portfolio (${res.status})`);
        }
        return;
      }
      const { portfolioId } = await res.json();
      showCustomAlert('newPortfolioModal.modalMessage.creationSuccess', false, true);
      onCreated(portfolioId);


    } catch (err) {
      console.error(err);
      showCustomAlert('newPortfolioModal.modalMessage.creationError', true);
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false); 
    } 
  };

  const isActionDisabled = auth.isLoading || !auth.isAuthenticated;

 //initialHoldings 프롭스의 변경을 감지하고 상태를 업데이트하는 useEffect
  // useEffect(() => {
  //   if (initialHoldings.length > 0) {
  //     setHoldings(initialHoldings);
  //   }
  // }, [initialHoldings]); 


  return (
    <div className={styles.modalBackdrop}>
      <div className={`${styles.modalContent} ${isDarkTheme ? styles.dark : ''}`}>
        <button
          className={styles.themeToggle}
          onClick={() => setIsDarkTheme(!isDarkTheme)}
        >
          {isDarkTheme ? '☀️' : '🌙'}
        </button>
        <button className={styles.closeButton} onClick={onClose}>×</button>
        <h2 className={styles.title}>{t('newPortfolioModal.title')}</h2>

        <div className={styles.fieldGroup}>
          <label htmlFor="portfolio-name">{t('newPortfolioModal.portfolioNameLabel')}</label>
          <input
            type="text"
            id="portfolio-name"
            placeholder={t('newPortfolioModal.portfolioNamePlaceholder')}
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={20}
            required
            disabled={isActionDisabled}
            title={isActionDisabled ? t('newPortfolioModal.loginRequiredTooltip') : ''}
          />
        </div>

        <form className={styles.form} onSubmit={e => e.preventDefault()}>
          <div className={styles.holdingHeader}>
            <div>{t("backtest.TickerAndWeight")}</div>
          </div>

          <div className={styles.holdingListContainer}>
            {holdings.map((h, i) => (
              <div key={i} className={styles.holdingRow}>
                <TickerRow
                  disableDefaultStyles
                  className={styles.inputTicker}
                  value={h.ticker}
                  onChange={val => updateHolding(i, 'ticker', val)}
                  existingTickers={holdings.filter((_, idx) => idx !== i).map(h => h.ticker).filter(Boolean)}
                  disabled={isActionDisabled}
                  title={isActionDisabled ? t('newPortfolioModal.loginRequiredTooltip') : ''}
                />
                <NumericFormat
                  className={styles.inputValue}
                  decimalScale={1}
                  fixedDecimalScale
                  allowNegative={false}
                  placeholder="25.0"
                  value={h.weight === 0 ? '' : h.weight * 100}
                  onValueChange={values => {
                    const pct = parseFloat(values.value) || 0;
                    updateHolding(i, 'weight', pct / 100);
                  }}
                  isAllowed={({ floatValue }) => {
                    if (floatValue === undefined) {
                      return true;
                    }
                    return floatValue >= 0 && floatValue <= 100;
                  }}
                  inputMode="numeric"
                  disabled={isActionDisabled}
                  title={isActionDisabled ? t('newPortfolioModal.loginRequiredTooltip') : ''}
                />
                <button
                  type="button"
                  className={styles.btnDelete}
                  onClick={() => removeHolding(i)}
                  aria-label={t('newPortfolioModal.deleteButton')}
                  disabled={isActionDisabled}
                  title={isActionDisabled ? t('newPortfolioModal.loginRequiredTooltip') : ''}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <div className={styles.totalWeight}>
            {t('newPortfolioModal.totalWeightLabel')} <strong>{(totalWeight * 100).toFixed(2)}%</strong>
            {!isTotalWeightValid && (
              <span className={styles.warningText}> {t('newPortfolioModal.weightWarning')}</span>
            )}
          </div>

          {/* 💡 [수정] isForcedPublic이 false일 때만 비공개 체크박스 표시 */}
           {!isForcedPublic && (
            <div className={styles.fieldGroup} style={{ marginTop: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id="makePublic" // 💡 ID를 'makePublic'으로 변경
                  checked={!isPrivate} // 💡 논리 반전: isPrivate가 true(비공개)면 체크 해제 상태
                  onChange={(e) => setIsPrivate(!e.target.checked)} // 💡 체크박스 상태를 isPrivate와 반대로 연동
                  disabled={isActionDisabled}
                  // style={{ marginRight: '5px' }}
                  style={{ marginRight: '10px', width: '40px', height: '40px' }}
                />
                <label htmlFor="makePublic" style={{ fontWeight: 'bold' }}>
                  {t('newPortfolioModal.trackPortfolio')}
                </label>
              </div>
       
            </div>
          )}
          {/* 💡 [수정] 끝 */}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.btnAdd}
              onClick={addHolding}
              disabled={isActionDisabled}
              title={isActionDisabled ? t('newPortfolioModal.loginRequiredTooltip') : ''}
            >
              {t('newPortfolioModal.addHolding')}
            </button>
            <button
              type="button"
              className={styles.btnCreate}
              onClick={() => {
                if (!isTotalWeightValid) {
                  showCustomAlert('newPortfolioModal.modalMessage.totalWeight', true);
                  return;
                }
                handleSubmit();
              }}
              disabled={isActionDisabled || isSubmitting}
              title={isActionDisabled ? t('newPortfolioModal.loginRequiredTooltip') : isSubmitting ? t('newPortfolioModal.modalMessage.submitting') : ''}
            >
              {t('newPortfolioModal.createButton')}
            </button>
          </div>
        </form>
      </div>

      {showMessageModal && (
        <MessageModal
          message={messageModalContent}
          onClose={closeCustomAlert}
          isError={isErrorMessage}
          isSuccess={isSuccessMessage}
        />
      )}
    </div>
  );
}